import { Button, FormControlLabel, Grid, Typography } from "@material-ui/core";
import useText from "constants/text";
import { isVehicleTruckChecked, ITourTemplateAxe, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { EVehiclesInput, EVehiculeType } from "generated/graphql";
import React from "react";
import Autocomplete from "ui/Autocomplete";
import StyledCheckbox from "ui/Checkbox";
import Dialog from "ui/Dialog";
import { CamionType } from "../../shared/CamionType";
import { ContainerRadioElement, DescriptionRadio, ErrorStep, TitleRadio } from "../../shared/MainStyles";
import { isFieldWithValue } from "../../shared/utils";
import VehicleCounter from "../../shared/VehicleCounter";



interface VehicleProps {
  templateAxe: ITourTemplateAxe;
  onClose?: () => void;
}

const VehicleType = ({ templateAxe, onClose }: VehicleProps) => {
  const { requestQuoteTour, setRequestQuoteTour, fieldError, truckType } = React.useContext(NewRequestQuoteContext);
  const { text } = useText();
  const vehicleTypes = [
    {
      type: EVehiculeType.Scooter,
      title: "SCOOTER",
      desc: text.requestQuote.desc_vehicle_1,
      enableCounter: true,
    },
    {
      type: EVehiculeType.MiniVan,
      title: "MINIVAN",
      desc: text.requestQuote.desc_vehicle_2,
      enableCounter: false,
    },
    {
      type: EVehiculeType.Van,
      title: "VAN",
      desc: text.requestQuote.desc_vehicle_3,
      enableCounter: false,
    },
    {
      type: "TRUCK",
      title: text.requestQuote.truck,
      desc: text.requestQuote.desc_vehicle_4,
      enableCounter: true,
    },
  ];

  const AllTemplateAxes = React.useMemo(() => {
    return requestQuoteTour?.templateAxes ? requestQuoteTour?.templateAxes : [];
  }, [requestQuoteTour]);

  const Axe = React.useMemo(() => {
    const activeAxe = AllTemplateAxes.find((el) => el?.name === templateAxe?.name);
    return activeAxe;
  }, [AllTemplateAxes, templateAxe]);

  const indexAxe = React.useMemo(() => AllTemplateAxes.indexOf(Axe), [AllTemplateAxes]);

  const handleChange = (vehicleType: string) => {
    const vehicles = (Axe?.vehicles as EVehiclesInput[]) || [];
    const selectedIndex = vehicles?.findIndex((v) => v?.vehicleType === vehicleType);
    let newSelected: Array<EVehiclesInput> = [];
    if (selectedIndex === -1) {
      newSelected = vehicles?.concat({
        vehicleType: vehicleType as EVehiculeType,
        numberOfVehicule: 1,
        price: 0,
        discount: 0,
      });
    } else if (selectedIndex >= 0) {
      newSelected = vehicles?.slice(0, selectedIndex).concat(vehicles?.slice(selectedIndex + 1));
    }
    const newAxe = {
      ...Axe,
      vehicles: newSelected,
    };
    const templateAxes = [...AllTemplateAxes];
    templateAxes[indexAxe] = newAxe;
    setRequestQuoteTour({ ...requestQuoteTour, templateAxes });
  };

  const onChangeVehicleNumber = (vehicleType: string, value: number) => {
    const vehicles = Axe?.vehicles?.map((v) =>
      v.vehicleType === vehicleType
        ? {
            ...v,
            vehicleType,
            numberOfVehicule: value,
          }
        : v
    );
    const newAxe = {
      ...Axe,
      vehicles,
    };
    const templateAxes = [...AllTemplateAxes];
    templateAxes[indexAxe] = newAxe;
    setRequestQuoteTour({ ...requestQuoteTour, templateAxes });
  };

  const vehicleError = React.useMemo(
    () => fieldError && !requestQuoteTour?.vehicles?.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldError, requestQuoteTour, truckType]
  );

  const isTruckChecked = React.useCallback(() => isVehicleTruckChecked(Axe?.vehicles as EVehiclesInput[]), [
    Axe?.vehicles,
  ]);

  const truckTypeError = React.useMemo(() => fieldError && isTruckChecked() && !isFieldWithValue(truckType), [
    fieldError,
    requestQuoteTour,
    truckType,
  ]);

  const isChecked = React.useCallback(
    (vehicleType: string) => {
      if (Axe?.vehicles?.length) {
        return (
          Axe?.vehicles?.findIndex((v) => {
            if (vehicleType.includes("TRUCK")) {
              return v.vehicleType.includes("TRUCK");
            }
            return v.vehicleType === vehicleType;
          }) !== -1
        );
      }
      return false;
    },
    [requestQuoteTour]
  );

  const getVehicleTypeCounter = React.useCallback(
    (vehicleType: string) => {
      if (Axe?.vehicles?.length) {
        if (vehicleType.includes("TRUCK")) {
          return Axe?.vehicles?.filter((v) => v.vehicleType?.includes("TRUCK"))[0]?.numberOfVehicule || 0;
        }
        return Axe?.vehicles?.filter((v) => v.vehicleType === vehicleType)[0]?.numberOfVehicule || 0;
      }
      return 0;
    },
    [Axe?.vehicles]
  );

  return (
    <NewRequestQuoteContext.Consumer>
      {({ setTruckType }) => (
        <>
          <Grid container spacing={4} style={{ padding: "10px 0rem 0rem 0rem" }}>
            {vehicleTypes.map((vehicle) => (
              <Grid item xs={12} key={vehicle.title}>
                <FormControlLabel
                  value={vehicle.type}
                  control={
                    <StyledCheckbox
                      checked={isChecked(vehicle.type)}
                      name={vehicle.type}
                      onChange={() => handleChange(vehicle.type)}
                      value={isChecked(vehicle.type)}
                    />
                  }
                  label={
                    <>
                      <ContainerRadioElement>
                        <TitleRadio>{vehicle.title}</TitleRadio>
                        {isChecked(vehicle.type) && (
                          <VehicleCounter
                            countValue={getVehicleTypeCounter(vehicle.type)}
                            onChangeCountValue={(value: any) => onChangeVehicleNumber(vehicle.type, value)}
                          />
                        )}
                      </ContainerRadioElement>
                      <Typography
                        variant="body2"
                        css={`
                          letter-spacing: normal;
                          line-height: 1.3;
                        `}
                      >
                        <DescriptionRadio>{vehicle.desc}</DescriptionRadio>
                      </Typography>
                    </>
                  }
                  css={`
                    align-items: flex-start;
                  `}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              {isTruckChecked() && (
                <Autocomplete
                  label={text.requestQuote.select_truck_type}
                  options={CamionType}
                  getOptionLabel={(option: any) => option.replace(/_/g, " ")}
                  getOptionSelected={(option: any, value: any) => option === value}
                  fullWidth
                  onChange={(event: any, value: any) => {
                    setTruckType(value);
                  }}
                  value={truckType}
                  error={truckTypeError}
                  helperText={truckTypeError ? text.requestQuote.truck_type_error : ""}
                />
              )}
            </Grid>
          </Grid>
          {vehicleError && <ErrorStep>{text.requestQuote.vehicle_type_error}</ErrorStep>}

          <Grid style={{ marginTop: 20 }} item spacing={1} container justifyContent="flex-end">
            <Grid item>
              <Button onClick={onClose} color="primary" variant="contained">
                {text.all.validate}
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </NewRequestQuoteContext.Consumer>
  );
};

interface Props {
  open: boolean;
  templateAxe: ITourTemplateAxe;
  onClose?: () => void;
}

export const AxeVehicleTypeDialog = ({ open, templateAxe, onClose }: Props) => {
  const {text} = useText();
  return (
    <Dialog handleClose={onClose} open={open} cancelButtonTitle="" title={text.requestQuote.axe_select_vehicle}>
      <VehicleType templateAxe={templateAxe} onClose={onClose} />
    </Dialog>
  );
};